import React, { useState, useEffect } from "react";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";

const EditFlightBookingModal = ({ bookingDetails, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    pnr: "",
    bookingNo: "",
    supplierBookingId: "",
    bookingStatus: "",
    netPrice: "",
    serviceFee: "",
    gst: "",
    gstAmount: "",
    totalPrice: "",
  });
  const [loading, setLoading] = useState(false);
  const [isTotalAutoCalculated, setIsTotalAutoCalculated] = useState(true);

  // Calculate total price whenever netPrice or serviceFee changes
  useEffect(() => {
    if (isTotalAutoCalculated) {
      const netPrice = parseFloat(formData.netPrice) || 0;
      const serviceFee = parseFloat(formData.serviceFee);
      const totalPrice = netPrice + serviceFee;

      setFormData((prev) => ({
        ...prev,
        totalPrice: totalPrice > 0 ? totalPrice.toFixed(2) : "",
      }));
    }
  }, [formData.netPrice, formData.serviceFee, isTotalAutoCalculated]);

  // Auto-calculate GST amount when serviceFee or gst percentage changes
  useEffect(() => {
    const serviceFee = parseFloat(formData.serviceFee);
    const gstPercentage = parseFloat(formData.gst);
    const calculatedGstAmount = (serviceFee * gstPercentage) / 100;

    if (serviceFee > 0 && gstPercentage > 0) {
      setFormData((prev) => ({
        ...prev,
        gstAmount: calculatedGstAmount.toFixed(2),
      }));
    }
  }, [formData.serviceFee, formData.gst]);

  useEffect(() => {
    if (bookingDetails) {
      // Debug: log the booking details to see actual structure
      console.log("Booking Details:", bookingDetails);

      // Calculate what the total should be based on fare and service fee
      const netPrice = bookingDetails.totalFare || 0;
      const serviceFee = bookingDetails.convenienceFee || 0;
      const existingTotalPrice = bookingDetails.totalPrice || 0;

      // Check if the existing total price matches the calculated one
      const calculatedTotal = netPrice + serviceFee;
      const shouldAutoCalculate = existingTotalPrice === calculatedTotal;

      setFormData({
        pnr: bookingDetails.pnr || "",
        bookingNo: bookingDetails.bookingNo || "",
        supplierBookingId: bookingDetails.supplierBookingId || "",
        bookingStatus: bookingDetails.bookingStatus || "",
        netPrice: netPrice.toString(),
        serviceFee: serviceFee.toString() || "",
        gst: bookingDetails.gst ? bookingDetails.gst.toString() : "18",
        gstAmount: bookingDetails.gstAmount
          ? bookingDetails.gstAmount.toString()
          : "",
        totalPrice: existingTotalPrice.toString(),
      });

      // Set auto-calculation based on whether total matches calculation
      setIsTotalAutoCalculated(shouldAutoCalculate);
    }
  }, [bookingDetails]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNumberInputChange = (e) => {
    const { name, value } = e.target;
    // Allow empty string for better UX, will be converted to 0 on submit
    setFormData((prev) => ({
      ...prev,
      [name]: value === "" ? "" : value,
    }));
  };

  // Manual override for GST amount
  const handleGstAmountChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      gstAmount: value === "" ? "" : value,
    }));
  };

  // Manual override for GST percentage
  const handleGstPercentageChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      gst: value === "" ? "" : value,
    }));
  };

  // Handle total price change - when user manually edits, disable auto-calculation
  const handleTotalPriceChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      totalPrice: value === "" ? "" : value,
    }));

    // Disable auto-calculation when user manually edits total price
    if (value !== "") {
      setIsTotalAutoCalculated(false);
    }
  };

  // Enable auto-calculation when netPrice or serviceFee is changed after manual edit
  const handleNetPriceOrServiceFeeChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === "" ? "" : value,
    }));

    // Re-enable auto-calculation if either netPrice or serviceFee is changed
    setIsTotalAutoCalculated(true);
  };

  // Reset auto-calculation if user wants to go back to calculated total
  const resetToAutoCalculate = () => {
    setIsTotalAutoCalculated(true);
    // This will trigger the auto-calculation useEffect
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {
        pnr: formData.pnr,
        bookingNo: formData.bookingNo,
        supplierBookingId: formData.supplierBookingId,
        bookingStatus: formData.bookingStatus,
        netPrice: parseFloat(formData.netPrice) || 0,
        serviceFee: parseFloat(formData.serviceFee) || 0,
        gst: parseFloat(formData.gst) || 0,
        gstAmount: parseFloat(formData.gstAmount) || 0,
        totalPrice: parseFloat(formData.totalPrice) || 0,
      };

      await apiService.update(
        `${config.UPDATEFLIGHTBOOKING}/${bookingDetails.flightBookingId}`,
        updateData
      );
      onSuccess();
    } catch (error) {
      console.error("Error updating flight booking:", error);
      alert("Error updating flight booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      tabIndex="-1"
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Edit Flight Booking</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">PNR</label>
                  <input
                    type="text"
                    className="form-control"
                    name="pnr"
                    value={formData.pnr}
                    onChange={handleInputChange}
                    placeholder="Enter PNR"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Booking No</label>
                  <input
                    type="text"
                    className="form-control"
                    name="bookingNo"
                    value={formData.bookingNo}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter Booking Number"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Supplier Confirmation</label>
                  <input
                    type="text"
                    className="form-control"
                    name="supplierBookingId"
                    value={formData.supplierBookingId}
                    onChange={handleInputChange}
                    placeholder="Enter Supplier Confirmation ID"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Booking Status</label>
                  <select
                    className="form-select"
                    name="bookingStatus"
                    value={formData.bookingStatus}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Status</option>
                    <option value="PENDING">PENDING</option>
                    <option value="CONFIRMED">CONFIRMED</option>
                    <option value="HOLD">HOLD</option>
                    <option value="CANCELLED">CANCELLED</option>
                  </select>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Net Price(Supplier Price)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    name="netPrice"
                    value={formData.netPrice}
                    onChange={handleNetPriceOrServiceFeeChange}
                    required
                    placeholder="0.00"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Service Fee (Markup)</label>
                  <input
                    type="text"
                    step="0.01"
                    className="form-control"
                    name="serviceFee"
                    value={formData.serviceFee}
                    onChange={handleNetPriceOrServiceFeeChange}
                    placeholder="0.00"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">GST (%)</label>
                  <input
                    type="text"
                    step="0.01"
                    className="form-control"
                    name="gst"
                    value={formData.gst}
                    onChange={handleGstPercentageChange}
                    placeholder="18.00"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">GST Amount</label>
                  <input
                    type="text"
                    step="0.01"
                    className="form-control"
                    name="gstAmount"
                    value={formData.gstAmount}
                    onChange={handleGstAmountChange}
                    placeholder="0.00"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Total Price</label>
                  <input
                    type="text"
                    step="0.01"
                    className="form-control"
                    name="totalPrice"
                    value={formData.totalPrice}
                    onChange={handleTotalPriceChange}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? "Updating..." : "Update Booking"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditFlightBookingModal;
